import { List } from "@/types/list";
import { Users, Calendar, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface ListCardProps {
  list: List;
  isSelected?: boolean;
  disableLinks?: boolean;
  className?: string;
  isFavorite?: boolean;
}

export const ListCard: React.FC<ListCardProps> = ({ 
  list, 
  isSelected = false, 
  disableLinks = false,
  className,
  isFavorite = false
}) => {
  const { t } = useTranslation();

  const TitleComponent = ({ children, to, className }: { children: React.ReactNode; to?: string; className?: string }) => {
    if (to && !disableLinks) {
      return <Link to={to} className={className}>{children}</Link>;
    }
    return <div className={className}>{children}</div>;
  };

  const ViewButtonComponent = ({ children, to, className }: { children: React.ReactNode; to?: string; className?: string }) => {
    if (to && !disableLinks) {
      return <Link to={to} className={className}>{children}</Link>;
    }
    return <div className={className}>{children}</div>;
  };

  return (
    <div className={cn(
      "relative flex flex-col h-full overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-sm bg-white/80 border border-white/20 rounded-sm",
      "hover:bg-white/90 hover:scale-[1.02] hover:border-white/30 hover:origin-center",
      "dark:bg-gray-800/80 dark:border-gray-700/20 dark:hover:bg-gray-800/90 dark:hover:border-gray-700/30",
      isSelected && "ring-2 ring-blue-500 border-blue-500",
      className,
      "w-full h-full"
    )}>
      <div className="p-4 border-b border-border flex flex-row justify-between items-start min-h-[60px]">
        <div className="flex-1 min-w-0">
          <TitleComponent 
            to={disableLinks ? undefined : `/lists/${list.id}`} 
            className={cn(
              "hover:text-primary transition-colors",
              disableLinks && "cursor-default"
            )}
          >
            <h3 className="text-lg font-semibold truncate mb-1 text-gray-900 dark:text-gray-100 flex items-center gap-2">
              {list.name}
              {isFavorite && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
            </h3>
          </TitleComponent>
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-between p-4 gap-2 pb-0">
        <div className="flex flex-col gap-1 min-h-[40px]">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {list.description || <span>&nbsp;</span>}
          </span>
        </div>
        <div className="flex flex-col gap-1 min-h-[48px]">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {list.candidate_count} candidate{list.candidate_count !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Created {format(new Date(list.created_at), 'MMM d, yyyy')}
            </span>
          </div>
        </div>
      </div>
      <div className="flex justify-end items-end p-4 pt-2">
        <ViewButtonComponent
          to={disableLinks ? undefined : `/lists/${list.id}`}
          className={cn(
            "inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-sm bg-blue-500 text-white hover:bg-blue-500 transition-colors dark:bg-blue-500 dark:hover:bg-blue-500",
            disableLinks && "cursor-default hover:bg-blue-500 dark:hover:bg-blue-500"
          )}
        >
          {t("contacts.view", "View")}
        </ViewButtonComponent>
      </div>
    </div>
  );
};
